/**
 * Narrowing the portfolio to the revisions someone is looking for.
 *
 * Pure but for its own state: the matching rules are testable without
 * rendering a table, and the screen keeps none of them.
 *
 * @module components/portfolio/usePortfolioFilters
 */

import { useMemo, useState } from "react";

import type { PortfolioProgrammeRevision } from "@/platform";

export type PortfolioStatusFilter = "all" | "current" | "approved-future" | "superseded";

export interface PortfolioFiltersModel {
  filtered: PortfolioProgrammeRevision[];
  /** Whether anything is narrowing the list, for the clear control. */
  active: boolean;
  clear: () => void;
  status: PortfolioStatusFilter;
  setStatus: (value: PortfolioStatusFilter) => void;
  level: string;
  setLevel: (value: string) => void;
  awardType: string;
  setAwardType: (value: string) => void;
  effectiveOn: string;
  setEffectiveOn: (value: string) => void;
  text: string;
  setText: (value: string) => void;
  /** The values actually present, so a filter never offers an empty result. */
  levels: string[];
  awardTypes: string[];
}

/**
 * The revisions in force on one date: per programme, the latest to have taken
 * effect by then. `undefined` for an empty date, meaning the filter is off.
 */
export function inForceOn(
  revisions: readonly PortfolioProgrammeRevision[],
  onDate: string,
): Set<string> | undefined {
  if (!onDate) {
    return undefined;
  }
  const latest = new Map<string, PortfolioProgrammeRevision>();
  revisions.forEach((revision) => {
    const current = latest.get(revision.programmeId);
    if (
      revision.effectiveFrom <= onDate &&
      (!current || revision.effectiveFrom > current.effectiveFrom)
    ) {
      latest.set(revision.programmeId, revision);
    }
  });
  return new Set([...latest.values()].map((revision) => revision.curriculumRevisionId));
}

/** Every field worth searching, run together and matched case-insensitively. */
export function matchesText(revision: PortfolioProgrammeRevision, query: string): boolean {
  if (!query) {
    return true;
  }
  return [
    revision.title,
    revision.programmeCode,
    revision.awardType,
    revision.institutionalLabel,
    revision.revisionNumber,
  ]
    .filter((value): value is string => Boolean(value))
    .join(" ")
    .toLocaleLowerCase()
    .includes(query.toLocaleLowerCase());
}

/**
 * @param revisions - Every approved revision the account can see.
 * @returns The narrowed list plus the controls that narrow it.
 */
export function usePortfolioFilters(
  revisions: readonly PortfolioProgrammeRevision[],
): PortfolioFiltersModel {
  const [status, setStatus] = useState<PortfolioStatusFilter>("all");
  const [pickedLevel, setLevel] = useState("all");
  const [pickedAwardType, setAwardType] = useState("all");
  const [effectiveOn, setEffectiveOn] = useState("");
  const [text, setText] = useState("");

  const levels = useMemo(
    () =>
      [...new Set(revisions.map((revision) => revision.nfqLevel).filter((n) => n !== null))]
        .sort((left, right) => left - right)
        .map(String),
    [revisions],
  );
  const awardTypes = useMemo(
    () => [...new Set(revisions.map((revision) => revision.awardType))].sort(),
    [revisions],
  );
  const inForce = useMemo(() => inForceOn(revisions, effectiveOn), [effectiveOn, revisions]);

  // A value the reloaded data no longer offers falls back to "all". The select
  // renders no matching option and so displays "All levels", and leaving the
  // filter applied would empty the table while every control reads unset.
  const level = levels.includes(pickedLevel) ? pickedLevel : "all";
  const awardType = awardTypes.includes(pickedAwardType) ? pickedAwardType : "all";

  const filtered = useMemo(
    () =>
      revisions.filter(
        (revision) =>
          (status === "all" || revision.lifecycleStatus === status) &&
          (level === "all" || String(revision.nfqLevel) === level) &&
          (awardType === "all" || revision.awardType === awardType) &&
          (!inForce || inForce.has(revision.curriculumRevisionId)) &&
          matchesText(revision, text.trim()),
      ),
    [awardType, inForce, level, revisions, status, text],
  );

  return {
    filtered,
    active:
      status !== "all" ||
      level !== "all" ||
      awardType !== "all" ||
      effectiveOn !== "" ||
      text.trim() !== "",
    clear: () => {
      setStatus("all");
      setLevel("all");
      setAwardType("all");
      setEffectiveOn("");
      setText("");
    },
    status,
    setStatus,
    level,
    setLevel,
    awardType,
    setAwardType,
    effectiveOn,
    setEffectiveOn,
    text,
    setText,
    levels,
    awardTypes,
  };
}
